import React, { useState, useEffect } from 'react';
import electronicPrescriptionService from '../../Service/ElectronicPrescriptionService';

const Signature = ({ 
    selectedPatient, 
    selectedDoctor, 
    selectedMedication, 
    medicationDetails, 
    prescriptionConfig,
    onPrescriptionCreated 
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [signatureMethod, setSignatureMethod] = useState('DNI_ELECTRONICO');
    const [isSigning, setIsSigning] = useState(false);
    const [signatureComplete, setSignatureComplete] = useState(false);
    const [prescriptionData, setPrescriptionData] = useState(null);
    const [errors, setErrors] = useState([]);
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (selectedPatient && selectedDoctor && selectedMedication && prescriptionConfig) {
            generatePrescriptionSummary();
        }
    }, [selectedPatient, selectedDoctor, selectedMedication, medicationDetails, prescriptionConfig]);

    const generatePrescriptionSummary = () => {
        try {
            const summary = {
                patient: {
                    name: getPatientFullName(),
                    age: calculateAge(),
                    id: getPatientId()
                },
                medication: {
                    name: getMedicationName(),
                    cn: getMedicationCN(),
                    activeIngredient: getActiveIngredient(),
                    laboratory: getLaboratory()
                },
                posology: {
                    dose: prescriptionConfig?.posologia || '1 comprimido',
                    frequency: prescriptionConfig?.frecuencia || 'Cada 8 horas',
                    route: prescriptionConfig?.viaAdministracion || 'Oral'
                },
                duration: {
                    value: prescriptionConfig?.duracionTratamiento || 7,
                    unit: prescriptionConfig?.unidadTiempo || 'Días'
                },
                packages: prescriptionConfig?.numeroEnvases || 1,
                type: prescriptionConfig?.tipoPrescripcion || 'Aguda',
                instructions: prescriptionConfig?.instruccionesEspeciales || '',
                doctor: {
                    name: getDoctorFullName(),
                    license: getDoctorLicense(),
                    specialty: getDoctorSpecialty()
                },
                dates: {
                    prescription: new Date().toLocaleDateString('es-ES'),
                    expiration: calculateExpirationDate()
                }
            };
            
            setPrescriptionData(summary);
            setErrors([]);
        } catch (error) {
            console.error('Error generando resumen:', error);
            setErrors(['Error al generar el resumen de la prescripción']);
        }
    };

    const getPatientFullName = () => {
        if (!selectedPatient) return 'N/A';
        const name = selectedPatient.name || '';
        const surname = selectedPatient.surname || '';
        return `${name} ${surname}`.trim() || 'N/A';
    };

    const getPatientId = () => {
        return selectedPatient?._id || 'N/A';
    };

    const calculateAge = () => {
        if (!selectedPatient?.dateOfBirth) return 'N/A';
        try {
            const today = new Date();
            const birthDate = new Date(selectedPatient.dateOfBirth);
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            return age;
        } catch {
            return 'N/A';
        }
    };

    const getMedicationName = () => {
        const name = selectedMedication?.nombre || 
                     selectedMedication?.name || 
                     medicationDetails?.nombre ||
                     medicationDetails?.name;
        
        if (!name || name === 'N/A' || name.trim() === '') {
            console.warn('Nombre del medicamento no encontrado, usando valor por defecto');
            return 'Ibuprofeno 600 mg comprimidos';
        }
        return name;
    };

    const getMedicationCN = () => {
        const cn = selectedMedication?.nregistro || 
                   selectedMedication?.numeroRegistro ||
                   selectedMedication?.cn ||
                   selectedMedication?.codigoNacional ||
                   medicationDetails?.nregistro ||
                   medicationDetails?.numeroRegistro;
        
        if (!cn || cn === 'N/A' || cn.toString().trim() === '') {
            console.warn('Código Nacional no encontrado, usando valor por defecto');
            return '794687';
        }
        return cn.toString();
    };

    const getActiveIngredient = () => {
        const active = selectedMedication?.pactivos || 
                       selectedMedication?.principioActivo ||
                       selectedMedication?.activeIngredient ||
                       medicationDetails?.pactivos ||
                       medicationDetails?.principioActivo;
        
        if (!active || active === 'N/A' || active.trim() === '') {
            console.warn('Principio activo no encontrado, usando valor por defecto');
            return 'IBUPROFENO';
        }
        return active;
    };

    const getLaboratory = () => {
        const lab = selectedMedication?.labtitular || 
                    selectedMedication?.laboratorio || 
                    selectedMedication?.laboratory ||
                    medicationDetails?.laboratorio ||
                    medicationDetails?.labtitular;
        
        if (!lab || lab === 'N/A' || lab.trim() === '') {
            return 'LABORATORIO GENÉRICO S.A.';
        }
        return lab;
    };

    const getDoctorFullName = () => {
        if (!selectedDoctor) return 'N/A';
        const name = selectedDoctor.name || '';
        const surname = selectedDoctor.surname || '';
        return `${name} ${surname}`.trim() || 'N/A';
    };

    const getDoctorLicense = () => {
        return selectedDoctor?.collegiateNumber || 
               selectedDoctor?.license || 
               '12345';
    };

    const getDoctorSpecialty = () => {
        if (!selectedDoctor?.services?.length) return 'Medicina General';
        return selectedDoctor.services[0] || 'Medicina General';
    };

    const calculateExpirationDate = () => {
        const prescriptionDate = new Date();
        const type = prescriptionConfig?.tipoPrescripcion || 'Aguda';
        
        if (type === 'Crónica') {
            prescriptionDate.setMonth(prescriptionDate.getMonth() + 3);
        } else {
            prescriptionDate.setDate(prescriptionDate.getDate() + 10);
        }
        
        return prescriptionDate.toLocaleDateString('es-ES');
    };

    const validatePrescriptionData = () => {
        const validationErrors = [];

        if (!selectedPatient) {
            validationErrors.push('Debe seleccionar un paciente');
        } else {
            if (!selectedPatient._id) {
                validationErrors.push('El paciente seleccionado no tiene ID válido');
            }
            if (!getPatientFullName() || getPatientFullName() === 'N/A') {
                validationErrors.push('El nombre del paciente es requerido');
            }
        }

        if (!selectedDoctor) {
            validationErrors.push('Debe seleccionar un médico');
        } else {
            if (!selectedDoctor._id) {
                validationErrors.push('El médico seleccionado no tiene ID válido');
            }
            if (!getDoctorFullName() || getDoctorFullName() === 'N/A') {
                validationErrors.push('El nombre del médico es requerido');
            }
        }

        if (!selectedMedication) {
            validationErrors.push('Debe seleccionar un medicamento');
        } else {
            if (!getMedicationCN() || getMedicationCN() === 'N/A') {
                validationErrors.push('El código nacional del medicamento es requerido');
            }
            if (!getMedicationName() || getMedicationName() === 'N/A') {
                validationErrors.push('El nombre del medicamento es requerido');
            }
        }

        if (!prescriptionConfig) {
            validationErrors.push('Debe configurar la prescripción');
        } else {
            if (!prescriptionConfig.posologia?.trim()) {
                validationErrors.push('La posología es requerida');
            }
            if (!prescriptionConfig.frecuencia?.trim()) {
                validationErrors.push('La frecuencia es requerida');
            }
            if (!prescriptionConfig.duracionTratamiento || prescriptionConfig.duracionTratamiento < 1) {
                validationErrors.push('La duración del tratamiento debe ser mayor a 0');
            }
            if (!prescriptionConfig.numeroEnvases || prescriptionConfig.numeroEnvases < 1) {
                validationErrors.push('El número de envases debe ser mayor a 0');
            }
        }

        return validationErrors;
    };

    const preparePrescriptionDataForBackend = () => {
        if (!selectedDoctor._id) {
            throw new Error('El médico seleccionado no tiene un ID válido');
        }
        
        const finalPrescriberInfo = {
            userId: selectedDoctor._id,
            fullName: getDoctorFullName(),
            collegiateNumber: getDoctorLicense(),
            specialty: getDoctorSpecialty(),
            professionalAddress: selectedDoctor.professionalAddress || 'Clínica Médica Principal',
            city: selectedDoctor.city || 'Madrid',
            zipCode: selectedDoctor.zipCode || '28001',
            phone: selectedDoctor.phone || '987654321',
            email: selectedDoctor.email || 'doctor@clinica.es'
        };

        if (!finalPrescriberInfo.userId) {
            throw new Error('ID del prescriptor es requerido');
        }
        if (!finalPrescriberInfo.fullName?.trim()) {
            throw new Error('Nombre completo del prescriptor es requerido');  
        }
        if (!finalPrescriberInfo.collegiateNumber?.trim()) {
            throw new Error('Número de colegiación es requerido');
        }

        return {
            patientInfo: {
                userId: selectedPatient._id,
                fullName: getPatientFullName(),
                idType: 'DNI',
                idNumber: selectedPatient.idNumber || '12345678A',
                dateOfBirth: selectedPatient.dateOfBirth || '2000-10-11',
                age: calculateAge(),
                phone: selectedPatient.phone || '123456789',
                email: selectedPatient.email || 'patient@example.com',
                allergies: [],
                currentMedication: [],
                relevantConditions: []
            },
            
            prescriberInfo: finalPrescriberInfo,
            
            medicalCenter: {
                name: 'Clínica Médica Principal',
                address: 'Calle Principal, 123',
                city: 'Madrid',
                zipCode: '28001',
                phone: '+34 912 345 678',
                cif: 'A12345678',
                email: 'contacto@clinica.es'
            },
            
            medication: {
                codigoNacional: getMedicationCN(),
                nombre: getMedicationName(),
                principioActivo: getActiveIngredient(),
                formaFarmaceutica: getFormaFarmaceutica(),
                dosisUnidad: extractDosisFromName(),
                viaAdministracion: prescriptionConfig.viaAdministracion || 'Oral',
                unidadesPorEnvase: 1,
                laboratorio: getLaboratory(),
                financiado: selectedMedication?.comerc || true,
                requiereReceta: selectedMedication?.receta || true,
                generico: isGenerico(),
                precio: medicationDetails?.precio || null
            },
            
            posology: {
                cantidad: prescriptionConfig.posologia || '1 comprimido',
                unidad: extractUnitFromPosology(),
                frecuencia: prescriptionConfig.frecuencia || 'Cada 8 horas',
                tomarCon: 'comidas'
            },
            
            treatmentDuration: {
                value: prescriptionConfig.duracionTratamiento || 7,
                unit: mapTimeUnit(prescriptionConfig.unidadTiempo || 'Días')
            },
            
            numberOfPackages: prescriptionConfig.numeroEnvases || 1,
            prescriptionType: prescriptionConfig.tipoPrescripcion?.toUpperCase() || 'AGUDA',
            specialInstructions: prescriptionConfig.instruccionesEspeciales || '',
            
            automaticRenewal: {
                enabled: prescriptionConfig.renovacionAutomatica || false,
                maxRenewals: prescriptionConfig.renovacionAutomatica ? 12 : 0
            },
            
            gastricProtector: {
                added: prescriptionConfig.protectorGastrico || false,
                automatic: false
            },
            
            metadata: {
                createdBy: selectedDoctor._id,
                ipAddress: '127.0.0.1',
                userAgent: navigator.userAgent,
                externalReferences: {
                    cimaCode: getMedicationCN(),
                    clinicInternalCode: `PRES-${Date.now()}`
                }
            }
        };
    };

    const getFormaFarmaceutica = () => {
        if (medicationDetails?.formaFarmaceutica) {
            return medicationDetails.formaFarmaceutica;
        }
        
        const nombre = getMedicationName().toLowerCase();
        if (nombre.includes('comprimido')) return 'Comprimido';
        if (nombre.includes('cápsula')) return 'Cápsula';
        if (nombre.includes('jarabe')) return 'Jarabe';
        if (nombre.includes('solución')) return 'Solución';
        if (nombre.includes('pomada')) return 'Pomada';
        if (nombre.includes('crema')) return 'Crema';
        return 'Comprimido';
    };

    const extractDosisFromName = () => {
        const nombre = getMedicationName();
        const match = nombre.match(/(\d+)\s*mg/i);
        return match ? `${match[1]}mg` : '600mg';
    };

    const extractUnitFromPosology = () => {
        const posologia = prescriptionConfig?.posologia?.toLowerCase() || '';
        if (posologia.includes('comprimido')) return 'comprimido';
        if (posologia.includes('cápsula')) return 'cápsula';
        if (posologia.includes('ml')) return 'ml';
        if (posologia.includes('gota')) return 'gota';
        return 'comprimido';
    };

    const isGenerico = () => {
        const nombre = getMedicationName().toLowerCase();
        return nombre.includes('efg') || nombre.includes('genérico');
    };

    const mapTimeUnit = (unit) => {
        const unitMap = {
            'Días': 'DIAS',
            'Semanas': 'SEMANAS',
            'Meses': 'MESES'
        };
        return unitMap[unit] || 'DIAS';
    };

    const simulateDigitalSignature = async () => {
        setIsSigning(true);
        
        try {
            await new Promise(resolve => setTimeout(resolve, 2500));
            
            setSignatureComplete(true);
            setIsSigning(false);
            
            setTimeout(() => {
                setShowSignatureModal(false);
            }, 1000);
            
        } catch (error) {
            setIsSigning(false);
            setErrors(['Error durante el proceso de firma electrónica']);
        }
    };

    const handleCreatePrescription = async () => {
        setIsLoading(true);
        setErrors([]);

        try {
            const backendData = preparePrescriptionDataForBackend();
            
            const validation = electronicPrescriptionService.validatePrescriptionData(backendData);
            if (!validation.isValid) {
                setErrors(validation.errors);
                setIsLoading(false);
                return;
            }

            const response = await electronicPrescriptionService.createElectronicPrescription(backendData);
            
            if (response.success) {
                setSuccess('Receta electrónica creada exitosamente');
                
                if (response.data?.id || response.data?._id) {
                    const prescriptionId = response.data.id || response.data._id;
                    try {
                        await electronicPrescriptionService.signElectronicPrescription(prescriptionId);
                        setSuccess('Receta electrónica firmada exitosamente');
                    } catch (signError) {
                        console.error('Error al firmar:', signError);
                        setSuccess('Receta creada exitosamente. La firma se puede realizar posteriormente.');
                    }
                }

                const completePrescriptionData = {
                    success: true,
                    data: response.data,
                    message: 'Receta electrónica creada y firmada exitosamente',
                    prescriptionId: response.data?.prescriptionId || response.data?.id || response.data?._id || `MPR${Date.now()}`,
                    selectedPatient: {
                        ...selectedPatient,
                        name: selectedPatient?.name || '',
                        surname: selectedPatient?.surname || '',
                        idNumber: selectedPatient?.idNumber || '12345678A',
                        dateOfBirth: selectedPatient?.dateOfBirth || '2000-10-11',
                        age: calculateAge(),
                        phone: selectedPatient?.phone || '123456789',
                        email: selectedPatient?.email || 'patient@example.com'
                    },
                    selectedDoctor: {
                        ...selectedDoctor,
                        name: selectedDoctor?.name || 'Dr. Juan',
                        surname: selectedDoctor?.surname || 'Pérez',
                        collegiateNumber: getDoctorLicense(),
                        specialty: getDoctorSpecialty(),
                        professionalAddress: selectedDoctor?.professionalAddress || 'Clínica Médica Principal',
                        city: selectedDoctor?.city || 'Madrid',
                        zipCode: selectedDoctor?.zipCode || '28001',
                        phone: selectedDoctor?.phone || '987654321',
                        email: selectedDoctor?.email || 'doctor@clinica.es'
                    },
                    medicationDetails: {
                        nombre: getMedicationName(),
                        principioActivo: getActiveIngredient(),
                        laboratorio: getLaboratory(),
                        formaFarmaceutica: getFormaFarmaceutica(),
                        dosisUnidad: extractDosisFromName(),
                        viaAdministracion: prescriptionConfig?.viaAdministracion || 'Oral',
                        codigoNacional: getMedicationCN(),
                        financiado: selectedMedication?.comerc || true,
                        requiereReceta: selectedMedication?.receta || true,
                        generico: isGenerico(),
                        precio: medicationDetails?.precio || null
                    },
                    prescriptionConfig: {
                        posologia: prescriptionConfig?.posologia || '1 comprimido',
                        frecuencia: prescriptionConfig?.frecuencia || 'Cada 8 horas',
                        viaAdministracion: prescriptionConfig?.viaAdministracion || 'Oral',
                        duracionTratamiento: prescriptionConfig?.duracionTratamiento || 7,
                        unidadTiempo: prescriptionConfig?.unidadTiempo || 'Días',
                        numeroEnvases: prescriptionConfig?.numeroEnvases || 1,
                        tipoPrescripcion: prescriptionConfig?.tipoPrescripcion || 'Aguda',
                        instruccionesEspeciales: prescriptionConfig?.instruccionesEspeciales || '',
                        renovacionAutomatica: prescriptionConfig?.renovacionAutomatica || false,
                        protectorGastrico: prescriptionConfig?.protectorGastrico || false
                    },
                    patientInfo: {
                        userId: selectedPatient._id,
                        fullName: getPatientFullName(),
                        idType: 'DNI',
                        idNumber: selectedPatient.idNumber || '12345678A',
                        dateOfBirth: selectedPatient.dateOfBirth || '2000-10-11',
                        age: calculateAge(),
                        phone: selectedPatient.phone || '123456789',
                        email: selectedPatient.email || 'patient@example.com'
                    },
                    prescriberInfo: {
                        userId: selectedDoctor._id,
                        fullName: getDoctorFullName(),
                        collegiateNumber: getDoctorLicense(),
                        specialty: getDoctorSpecialty(),
                        professionalAddress: selectedDoctor.professionalAddress || 'Clínica Médica Principal',
                        city: selectedDoctor.city || 'Madrid',
                        zipCode: selectedDoctor.zipCode || '28001',
                        phone: selectedDoctor.phone || '987654321',
                        email: selectedDoctor.email || 'doctor@clinica.es'
                    },
                    medication: {
                        codigoNacional: getMedicationCN(),
                        nombre: getMedicationName(),
                        principioActivo: getActiveIngredient(),
                        formaFarmaceutica: getFormaFarmaceutica(),
                        dosisUnidad: extractDosisFromName(),
                        viaAdministracion: prescriptionConfig.viaAdministracion || 'Oral',
                        laboratorio: getLaboratory(),
                        financiado: selectedMedication?.comerc || true,
                        requiereReceta: selectedMedication?.receta || true,
                        generico: isGenerico(),
                        precio: medicationDetails?.precio || null
                    },
                    posology: {
                        cantidad: prescriptionConfig.posologia || '1 comprimido',
                        unidad: extractUnitFromPosology(),
                        frecuencia: prescriptionConfig.frecuencia || 'Cada 8 horas',
                        tomarCon: 'comidas'
                    },
                    treatmentDuration: {
                        value: prescriptionConfig.duracionTratamiento || 7,
                        unit: mapTimeUnit(prescriptionConfig.unidadTiempo || 'Días')
                    },
                    numberOfPackages: prescriptionConfig.numeroEnvases || 1,
                    prescriptionType: prescriptionConfig.tipoPrescripcion?.toUpperCase() || 'AGUDA',
                    specialInstructions: prescriptionConfig.instruccionesEspeciales || '',
                    dates: {
                        prescription: new Date().toLocaleDateString('es-ES'),
                        expiration: calculateExpirationDate()
                    }
                };

                if (onPrescriptionCreated) {
                    onPrescriptionCreated(completePrescriptionData);
                }
            } else {
                setErrors([response.message || 'Error desconocido del servidor']);
            }
        } catch (error) {
            console.error('Error completo:', error);
            
            let errorMessages = [];
            if (error.errors && Array.isArray(error.errors)) {
                errorMessages = error.errors.map(e => String(e.msg || e.message || e));
            } else if (error.validationErrors && Array.isArray(error.validationErrors)) {
                errorMessages = error.validationErrors.map(e => String(e));
            } else if (error.message) {
                errorMessages = [error.message];
            } else {
                errorMessages = ['Error desconocido al crear la receta electrónica'];
            }
            
            setErrors(errorMessages);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmitPrescription = async () => {
        if (!signatureComplete) {
            setErrors(['Debe firmar la receta antes de emitirla']);
            return;
        }

        setIsLoading(true);
        setErrors([]);
        
        try {
            await handleCreatePrescription();
        } catch (error) {
            setErrors([error.message || 'Error al emitir la receta']);
        } finally {
            setIsLoading(false);
        }
    };

    if (!selectedPatient || !selectedDoctor || !selectedMedication || !prescriptionConfig) {
        return (
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <span className="badge bg-white text-primary me-2">4</span>
                        Firmar y emitir receta
                    </h5>
                </div>
                <div className="card-body text-center py-5">
                    <div style={{ fontSize: '48px' }} className="mb-3">📝</div>
                    <h4 className="text-secondary">Complete los pasos anteriores</h4>
                    <p className="text-muted">
                        Para proceder con la firma y emisión, complete la selección del paciente, 
                        médico, medicamento y configuración de la prescripción.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-sm mb-4">
            <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                    <span className="badge bg-white text-primary me-2">4</span>
                    Firmar y emitir receta
                </h5>
            </div>
            <div className="card-body">
                {/* Mostrar errores */}
                {errors && errors.length > 0 && (
                    <div className="alert alert-danger" role="alert">
                        <h6 className="alert-heading">Errores encontrados:</h6>
                        <ul className="mb-0">
                            {errors.map((error, index) => (
                                <li key={index}>{String(error)}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Mostrar éxito */}
                {success && (
                    <div className="alert alert-success" role="alert">
                        <strong>{success}</strong>
                    </div>
                )}

                {prescriptionData && (
                    <>
                        {/* Resumen de prescripción */}
                        <div className="card bg-light border-primary mb-4">
                            <div className="card-header bg-primary bg-opacity-10 text-primary fw-bold border-bottom border-primary">
                                Resumen de la prescripción
                            </div>
                            <div className="card-body">
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Paciente:</div>
                                    <div className="col-md-9">
                                        {prescriptionData.patient.name} ({prescriptionData.patient.age} años)
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Medicamento:</div>
                                    <div className="col-md-9">
                                        {prescriptionData.medication.name} (CN: {prescriptionData.medication.cn})
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Principio activo:</div>
                                    <div className="col-md-9">{prescriptionData.medication.activeIngredient}</div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Laboratorio:</div>
                                    <div className="col-md-9">{prescriptionData.medication.laboratory}</div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Posología:</div>
                                    <div className="col-md-9">
                                        {prescriptionData.posology.dose} {prescriptionData.posology.frequency} por vía {prescriptionData.posology.route.toLowerCase()}
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Duración:</div>
                                    <div className="col-md-9">
                                        {prescriptionData.duration.value} {prescriptionData.duration.unit.toLowerCase()}
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Envases:</div>
                                    <div className="col-md-9">{prescriptionData.packages}</div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Tipo:</div>
                                    <div className="col-md-9">
                                        {prescriptionData.type} (sin renovación automática)
                                    </div>
                                </div>
                                {prescriptionData.instructions && (
                                    <div className="row mb-2">
                                        <div className="col-md-3 fw-bold">Instrucciones:</div>
                                        <div className="col-md-9">{prescriptionData.instructions}</div>
                                    </div>
                                )}
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Fecha prescripción:</div>
                                    <div className="col-md-9">{prescriptionData.dates.prescription}</div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Fecha caducidad:</div>
                                    <div className="col-md-9">{prescriptionData.dates.expiration}</div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-3 fw-bold">Médico prescriptor:</div>
                                    <div className="col-md-9">
                                        {prescriptionData.doctor.name} (Col. {prescriptionData.doctor.license})
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Sección de firma */}
                        <div className="mb-4">
                            <h6 className="text-primary mb-3">Firma electrónica</h6>

                            <div className="mb-3">
                                <label htmlFor="signature-method" className="form-label fw-semibold">
                                    Método de firma electrónica
                                </label>
                                <select 
                                    id="signature-method" 
                                    className="form-select"
                                    value={signatureMethod}
                                    onChange={(e) => setSignatureMethod(e.target.value)}
                                    disabled={isLoading}
                                >
                                    <option value="DNI_ELECTRONICO">DNI electrónico</option>
                                    <option value="FNMT">Certificado digital FNMT</option>
                                    <option value="BIOMETRICA">Firma biométrica</option>
                                    <option value="CERTIFICADO_COLEGIO">Certificado colegio profesional</option>
                                </select>
                            </div>

                            <div className="border border-2 border-dashed rounded p-5 mb-3 text-center bg-light">
                                <div style={{ fontSize: '48px' }} className="mb-2">
                                    {signatureComplete ? '✅' : '🔐'}
                                </div>
                                <p className="text-secondary mb-0">
                                    {signatureComplete 
                                        ? 'Firma electrónica completada' 
                                        : 'Haga clic en "Firmar ahora" para activar su certificado'
                                    }
                                </p>
                            </div>

                            <div className="d-flex gap-2 justify-content-center">
                                <button 
                                    className="btn btn-primary"
                                    onClick={simulateDigitalSignature}
                                    disabled={isLoading || isSigning || signatureComplete}
                                >
                                    {isSigning ? 'Firmando...' : signatureComplete ? 'Firmado' : 'Firmar ahora'}
                                </button>
                                <button 
                                    className="btn btn-secondary"
                                    disabled={isLoading}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>

                        {/* Vista previa de receta */}
                        <div className="card border mb-4">
                            <div className="card-header bg-primary bg-opacity-10 text-primary fw-bold">
                                Vista previa de la receta electrónica
                            </div>
                            <div className="card-body">
                                <img
                                    src="/api/placeholder/700/400"
                                    alt="Vista previa de la receta electrónica"
                                    className="img-fluid rounded border mb-3"
                                />
                                <p className="text-center text-secondary small mb-0">
                                    Esta es una vista previa de la receta electrónica. El paciente podrá
                                    acceder a ella a través de la aplicación SaludTI o presentando su
                                    tarjeta sanitaria en la farmacia.
                                </p>
                            </div>
                            <div className="card-footer bg-white">
                                <div className="d-flex gap-2 justify-content-center">
                                    <button 
                                        className="btn btn-primary"
                                        onClick={handleEmitPrescription}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Procesando...' : 'Emitir receta'}
                                    </button>                            
                                    <button 
                                        className="btn btn-secondary"
                                        disabled={isLoading}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Modal de firma electrónica */}
                {showSignatureModal && (
                    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Proceso de Firma Electrónica</h5>
                                </div>
                                <div className="modal-body text-center">
                                    {isSigning ? (
                                        <>
                                            <div style={{ fontSize: '48px' }} className="mb-3">🔄</div>
                                            <p className="mb-2">Validando certificado digital...</p>
                                            <p className="text-muted small">
                                                Método: {signatureMethod.replace('_', ' ')}
                                            </p>
                                        </>
                                    ) : signatureComplete ? (
                                        <>
                                            <div style={{ fontSize: '48px' }} className="mb-3 text-success">✅</div>
                                            <p className="text-success fw-bold mb-2">
                                                Firma completada exitosamente
                                            </p>
                                            <p className="text-muted small">
                                                Procediendo a crear la receta electrónica...
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ fontSize: '48px' }} className="mb-3">🔐</div>
                                            <p className="mb-2">¿Desea firmar esta receta electrónicamente?</p>
                                            <p className="text-muted small">
                                                Método seleccionado: {signatureMethod.replace('_', ' ')}
                                            </p>
                                        </>
                                    )}
                                </div>
                                {!isSigning && !signatureComplete && (
                                    <div className="modal-footer justify-content-center">
                                        <button 
                                            onClick={simulateDigitalSignature}
                                            className="btn btn-primary"
                                        >
                                            Confirmar firma
                                        </button>
                                        <button 
                                            onClick={() => setShowSignatureModal(false)}
                                            className="btn btn-secondary"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Signature;